import { useState, useRef } from "react";
import axios from "axios";

const api = axios.create({ baseURL: "http://localhost:8000" });

interface ColumnMeta {
  name: string;
  description: string;
  dtype: string;
  n_unique: number;
  missing_pct: number;
  mean: number | null;
  std: number | null;
}

interface DatasetInfo {
  dataset_id: string;
  name: string;
  n_rows: number;
  n_cols: number;
  variables: ColumnMeta[];
}

interface MatchResult {
  expert_variable: string;
  match_type: "direct" | "proxy" | "derived" | "missing";
  matched_columns: string[];
  confidence: number;
  reasoning: string;
  transformation: string;
  code_hint: string;
}

const MATCH_COLORS = {
  direct: { color: "#10b981", bg: "rgba(16,185,129,0.08)", label: "Direct Match", icon: "✓" },
  proxy: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", label: "Proxy", icon: "~" },
  derived: { color: "#6366f1", bg: "rgba(99,102,241,0.08)", label: "Derived", icon: "⚙" },
  missing: { color: "#ef4444", bg: "rgba(239,68,68,0.08)", label: "Missing", icon: "✗" },
};

/* ── Sample expert variables for demo ── */
const DEMO_EXPERT_VARS = [
  { name: "humidity_gt_65", description: "Humidity above 65% drives sales regardless of temperature", threshold: "above 65%", certainty: 0.85, source_quote: "Above 65%, sales spike regardless of temperature" },
  { name: "competitor_gt_3", description: "More than 3 competitors within half a mile kills the store", threshold: "above 3", certainty: 0.80, source_quote: "Above three within half a mile, I've watched it kill stores" },
  { name: "families_under12_gt_3000", description: "Need at least 3000 families with kids under 12 within a mile", threshold: "above 3000", certainty: 0.88, source_quote: "Below 2,000 households the store struggles" },
  { name: "heat_index_high", description: "When heat index (combined temp and humidity) exceeds 95, people crave ice cream most", threshold: "above 95", certainty: 0.75, source_quote: "It's not just hot days — it's the muggy ones" },
  { name: "manager_tenure_gt_24mo", description: "Store manager with more than 2 years tenure signals stability", threshold: "above 24 months", certainty: 0.70, source_quote: "When a manager stays more than two years that store is going to be fine" },
];

export default function DatasetMatcher() {
  const [dataset, setDataset] = useState<DatasetInfo | null>(null);
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    setLoading(true);
    setError(null);
    setMatches([]);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target_column", "Monthly_Revenue");
      const res = await api.post("/api/dataset/upload", form);
      setDataset(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Upload failed");
    }
    setLoading(false);
  };

  const handleMatch = async () => {
    if (!dataset) return;
    setMatching(true);
    setError(null);
    try {
      const res = await api.post(`/api/dataset/${dataset.dataset_id}/match-variables`, {
        expert_variables: DEMO_EXPERT_VARS,
      });
      setMatches(res.data.matches);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Matching failed");
    }
    setMatching(false);
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!dataset || matches.length === 0) return;
    setExporting(true);
    try {
      const codeHints = matches
        .filter(m => m.code_hint && !m.code_hint.startsWith("# Cannot"))
        .map(m => ({ expert_variable: m.expert_variable, code_hint: m.code_hint }));

      const res = await api.post(
        `/api/dataset/${dataset.dataset_id}/enrich-export`,
        { code_hints: codeHints },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataset.name.replace(/\.[^.]+$/, "")}_enriched.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError("Export failed");
    }
    setExporting(false);
  };

  const counts = {
    direct: matches.filter(m => m.match_type === "direct").length,
    proxy: matches.filter(m => m.match_type === "proxy").length,
    derived: matches.filter(m => m.match_type === "derived").length,
    missing: matches.filter(m => m.match_type === "missing").length,
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
          Dataset Scanner
        </h2>
        <p style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", margin: "3px 0 0" }}>
          Upload a dataset. We scan its metadata and match expert variables to your columns.
        </p>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "20px 32px 40px" }}>
        {/* Upload area */}
        {!dataset && (
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#1e3a6e"; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)"; }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
              const file = e.dataTransfer.files[0];
              if (file) handleUpload(file);
            }}
            style={{
              border: "2px dashed rgba(0,0,0,0.12)", borderRadius: 16, padding: "48px 32px",
              textAlign: "center", cursor: "pointer", background: "rgba(0,0,0,0.01)",
              transition: "all 0.2s ease", maxWidth: 600, margin: "20px auto",
            }}
          >
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden
              onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>↑</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
              {loading ? "Scanning..." : "Drop your Excel or CSV file here"}
            </div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.4)" }}>
              .xlsx and .csv supported — field description sheets auto-detected
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(239,68,68,0.08)", color: "#ef4444", fontSize: 12, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Dataset scanned — show columns */}
        {dataset && matches.length === 0 && (
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <div style={{
              padding: "16px 20px", borderRadius: 12, background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.15)", marginBottom: 20,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{dataset.name}</div>
                <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)", marginTop: 2 }}>
                  {dataset.n_rows} rows · {dataset.n_cols} columns · field descriptions {dataset.variables[0]?.description?.includes("Column from") ? "not found" : "detected"}
                </div>
              </div>
              <button onClick={() => { setDataset(null); setMatches([]); }} style={{
                padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)",
                background: "#fff", fontSize: 11, cursor: "pointer",
              }}>Change file</button>
            </div>

            {/* Column preview */}
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 8 }}>
              Detected columns
            </div>
            <div style={{
              border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12, overflow: "hidden", marginBottom: 24,
            }}>
              {dataset.variables.map((col, i) => (
                <div key={col.name} style={{
                  padding: "8px 16px", display: "flex", alignItems: "center", gap: 12,
                  borderTop: i > 0 ? "1px solid rgba(0,0,0,0.04)" : "none",
                  fontSize: 12,
                }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, flex: "0 0 220px" }}>{col.name}</span>
                  <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "rgba(0,0,0,0.04)", color: "rgba(0,0,0,0.45)", fontFamily: "'IBM Plex Mono', monospace" }}>
                    {col.dtype}
                  </span>
                  <span style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", flex: 1 }}>{col.description}</span>
                  {col.mean !== null && (
                    <span style={{ fontSize: 10, color: "rgba(0,0,0,0.3)", fontFamily: "'IBM Plex Mono', monospace" }}>
                      μ={col.mean.toFixed(1)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Match button */}
            <div style={{ textAlign: "center" }}>
              <button onClick={handleMatch} disabled={matching} style={{
                padding: "14px 32px", borderRadius: 120,
                background: matching ? "rgba(0,0,0,0.1)" : "linear-gradient(135deg, #1e3a6e, #3b7dd8)",
                color: matching ? "rgba(0,0,0,0.4)" : "#fff", border: "none",
                fontSize: 14, fontWeight: 600, cursor: matching ? "default" : "pointer",
                boxShadow: matching ? "none" : "0 4px 20px rgba(30,58,110,0.25)",
                transition: "all 0.2s ease",
              }}>
                {matching ? "Matching with Claude..." : "Match Expert Variables to Columns"}
              </button>
              <div style={{ fontSize: 11, color: "rgba(0,0,0,0.35)", marginTop: 8 }}>
                {DEMO_EXPERT_VARS.length} expert variables from the ice cream store interview
              </div>
            </div>
          </div>
        )}

        {/* Match results */}
        {matches.length > 0 && (
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            {/* Summary bar */}
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              {(["direct", "proxy", "derived", "missing"] as const).map(type => {
                const cfg = MATCH_COLORS[type];
                return (
                  <div key={type} style={{
                    flex: 1, padding: "12px 14px", borderRadius: 10,
                    background: cfg.bg, border: `1px solid ${cfg.color}20`,
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>
                      {counts[type]}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: cfg.color }}>{cfg.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Dataset info reminder */}
            <div style={{
              padding: "10px 16px", borderRadius: 10, background: "rgba(0,0,0,0.02)",
              marginBottom: 16, display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(0,0,0,0.45)",
            }}>
              <span>{dataset?.name} · {dataset?.n_rows} rows · {dataset?.n_cols} columns</span>
              <button onClick={() => { setDataset(null); setMatches([]); }} style={{
                background: "none", border: "none", fontSize: 11, color: "#1e3a6e", cursor: "pointer", fontWeight: 600,
              }}>Upload different file</button>
            </div>

            {/* Match cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {matches.map((match, idx) => {
                const cfg = MATCH_COLORS[match.match_type] || MATCH_COLORS.missing;
                const isExpanded = expandedIdx === idx;

                return (
                  <div key={idx} style={{
                    border: `1px solid ${isExpanded ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.06)"}`,
                    borderRadius: 12, overflow: "hidden", background: "#fff",
                    borderLeft: `4px solid ${cfg.color}`,
                    boxShadow: isExpanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
                    transition: "all 0.2s ease",
                  }}>
                    {/* Row header */}
                    <div
                      onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                      style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                          <span style={{
                            fontSize: 14, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
                          }}>{match.expert_variable}</span>
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                            background: cfg.bg, color: cfg.color,
                          }}>{cfg.icon} {cfg.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)" }}>
                          {match.match_type !== "missing"
                            ? <>→ <strong style={{ color: "#1e3a6e" }}>{match.matched_columns.join(" + ")}</strong></>
                            : "No matching column found"
                          }
                        </div>
                      </div>

                      {/* Confidence */}
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{
                          fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
                          color: match.confidence > 0.7 ? "#10b981" : match.confidence > 0.4 ? "#f59e0b" : "#ef4444",
                        }}>
                          {(match.confidence * 100).toFixed(0)}%
                        </div>
                        <div style={{ fontSize: 9, color: "rgba(0,0,0,0.35)" }}>confidence</div>
                      </div>

                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                        style={{ opacity: 0.2, transition: "transform 0.2s ease", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
                          {/* Reasoning */}
                          <div style={{
                            padding: "12px 16px", borderRadius: 10,
                            background: "rgba(0,0,0,0.02)", borderLeft: `3px solid ${cfg.color}40`,
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 6 }}>
                              Why this match
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.7, color: "rgba(0,0,0,0.65)" }}>
                              {match.reasoning}
                            </div>
                          </div>

                          {/* Transformation */}
                          <div style={{
                            padding: "12px 16px", borderRadius: 10,
                            background: "rgba(0,0,0,0.02)", borderLeft: "3px solid rgba(99,102,241,0.3)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 6 }}>
                              Transformation
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.7, color: "rgba(0,0,0,0.65)" }}>
                              {match.transformation}
                            </div>
                          </div>
                        </div>

                        {/* Code hint */}
                        {match.code_hint && !match.code_hint.startsWith("# Cannot") && (
                          <div style={{
                            marginTop: 12, padding: "12px 16px", borderRadius: 10,
                            background: "rgba(0,0,0,0.03)", fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 12, color: "#1e3a6e", lineHeight: 1.6,
                            border: "1px solid rgba(30,58,110,0.1)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 6, fontFamily: "'Inter', sans-serif" }}>
                              Pandas code
                            </div>
                            {match.code_hint}
                          </div>
                        )}

                        {/* Expert quote */}
                        {DEMO_EXPERT_VARS.find(v => v.name === match.expert_variable)?.source_quote && (
                          <div style={{
                            marginTop: 12, padding: "10px 16px", borderRadius: 10,
                            background: "rgba(30,58,110,0.03)", borderLeft: "3px solid rgba(30,58,110,0.2)",
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 4 }}>
                              Expert said
                            </div>
                            <div style={{ fontSize: 12, fontStyle: "italic", color: "rgba(0,0,0,0.55)" }}>
                              "{DEMO_EXPERT_VARS.find(v => v.name === match.expert_variable)?.source_quote}"
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Export button */}
            <div style={{
              marginTop: 28, padding: "28px 24px", borderRadius: 16,
              background: "linear-gradient(135deg, rgba(30,58,110,0.04), rgba(59,125,216,0.06))",
              border: "1px solid rgba(30,58,110,0.1)",
              textAlign: "center",
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                Ready to export
              </div>
              <div style={{ fontSize: 13, color: "rgba(0,0,0,0.5)", marginBottom: 18, maxWidth: 500, margin: "0 auto 18px" }}>
                Download your original dataset with {matches.filter(m => m.code_hint && !m.code_hint.startsWith("# Cannot")).length} new
                feature columns appended. Take it back to your pipeline and run your model.
              </div>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={{
                  padding: "14px 36px", borderRadius: 120,
                  background: exporting ? "rgba(0,0,0,0.1)" : "linear-gradient(135deg, #1e3a6e, #3b7dd8)",
                  color: exporting ? "rgba(0,0,0,0.4)" : "#fff", border: "none",
                  fontSize: 15, fontWeight: 600, cursor: exporting ? "default" : "pointer",
                  boxShadow: exporting ? "none" : "0 4px 20px rgba(30,58,110,0.25)",
                  transition: "all 0.2s ease",
                }}
              >
                {exporting ? "Building file..." : "Download Enriched .xlsx"}
              </button>
              <div style={{ fontSize: 11, color: "rgba(0,0,0,0.35)", marginTop: 10 }}>
                Includes a "New_Features" sheet documenting what was added and why
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
